/**
 * Renders the small Markdown subset the AI agent writes (bold, inline code, links, bullet lists, pipe
 * tables, headings, line breaks) to HTML. Everything is HTML-escaped first, so text from the agent or from
 * WMS data can never inject markup. Links are kept only when they point inside the app (`#/...`) or to an
 * https URL (e.g. a carrier tracking page); anything else is shown as plain text.
 */
export function renderAgentMarkdown(text: string): string {
  const escaped = escapeHtml(text || '');
  const lines = escaped.split(/\r?\n/);
  const html: string[] = [];
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // A pipe table: a header row, a |---| separator row, then body rows.
    if (isTableRow(line) && i + 1 < lines.length && isSeparatorRow(lines[i + 1])) {
      if (inList) {
        html.push('</ul>');
        inList = false;
      }
      const header = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && isTableRow(lines[i])) {
        rows.push(cells(lines[i]));
        i++;
      }
      i--;
      html.push(
        '<div class="md-table-wrap"><table class="md-table"><thead><tr>' +
          header.map((c) => `<th>${inline(c)}</th>`).join('') +
          '</tr></thead><tbody>' +
          rows.map((r) => '<tr>' + header.map((_, k) => `<td>${inline(r[k] ?? '')}</td>`).join('') + '</tr>').join('') +
          '</tbody></table></div>'
      );
      continue;
    }

    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    if (bullet) {
      if (!inList) {
        html.push('<ul>');
        inList = true;
      }
      html.push(`<li>${inline(bullet[1])}</li>`);
      continue;
    }
    if (inList) {
      html.push('</ul>');
      inList = false;
    }
    const heading = line.match(/^\s*#{1,4}\s+(.*)$/);
    if (heading) {
      html.push(`<div class="md-heading">${inline(heading[1])}</div>`);
      continue;
    }
    html.push(line.trim() === '' ? '<br>' : `${inline(line)}<br>`);
  }
  if (inList) {
    html.push('</ul>');
  }

  return html.join('').replace(/(<br>)+$/, '');
}

function isTableRow(line: string): boolean {
  const t = line.trim();
  return t.startsWith('|') && t.endsWith('|') && t.length > 2;
}

function isSeparatorRow(line: string): boolean {
  return /^\s*\|(\s*:?-{2,}:?\s*\|)+\s*$/.test(line);
}

function cells(line: string): string[] {
  return line.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
}

function inline(text: string): string {
  return text
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_match, label: string, url: string) => {
      if (url.startsWith('#/')) {
        return `<a href="${url}">${label}</a>`;
      }
      if (url.startsWith('https://')) {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`;
      }
      return label;
    });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
